import React, { useMemo } from "react";
import { Pause, Play } from "lucide-react";

import { buildAudioWaveBars } from "@fitness-app/client-shared/features/chat/audio-wave-bars";
import { useAudioPlayer } from "@fitness-app/client-shared/features/chat/use-audio-player";
import { type FileMessageContent } from "@fitness-app/data-models/entities/Chat";
import { formatMediaDurationFromSeconds } from "@fitness-app/utils/src/media/formatMediaDuration";
import { cn } from "@fitness-app/utils/src/styles/cn";

import { AudioWaveform } from "./AudioWaveform";

interface AudioAttachmentProps {
  /** An uploaded attachment; a file still uploading has no URL to play yet. */
  file: Pick<FileMessageContent, "duration" | "metering"> & { url: string };
  position?: "left" | "right";
}

/**
 * A voice message as its own bubble: play button, the recording's waveform (scrubbable) and the
 * time, coloured like the text bubble on the same side (client in blue, trainer in grey).
 */
export const AudioAttachment = ({ file, position }: AudioAttachmentProps): React.ReactElement => {
  const own = position === "right";
  const bars = useMemo(() => buildAudioWaveBars(file.metering), [file.metering]);
  const { isPlaying, currentTime, duration, progress, playbackRate, toggle, seek, cyclePlaybackRate } = useAudioPlayer({
    src: file.url,
    fallbackDurationSeconds: file.duration,
  });

  const shownTime = isPlaying || currentTime > 0 ? currentTime : duration;

  return (
    <div className={cn("flex px-3 py-1", own ? "justify-end" : "justify-start")}>
      <div
        className={cn(
          "flex w-[280px] max-w-full items-center gap-2.5 rounded-2xl py-2 pl-2 pr-3",
          own ? "bg-gray-200 text-gray-800" : "bg-blue-400 text-white",
        )}
      >
        <button
          type="button"
          onClick={toggle}
          aria-label={isPlaying ? "Zatrzymaj" : "Odtwórz"}
          className={cn(
            "flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full border-0 p-0 transition-transform focus-visible:outline-none focus-visible:ring-2 active:scale-95",
            own ? "bg-blue-500 text-white ring-blue-300" : "bg-white text-blue-500 ring-white/70",
          )}
        >
          {isPlaying ? (
            <Pause size={16} fill="currentColor" strokeWidth={0} />
          ) : (
            <Play size={16} fill="currentColor" strokeWidth={0} className="ml-0.5" />
          )}
        </button>

        <AudioWaveform
          bars={bars}
          progress={progress}
          seek={seek}
          playedClassName={own ? "bg-blue-500" : "bg-white"}
          unplayedClassName={own ? "bg-gray-400/70" : "bg-white opacity-40"}
        />

        <span className="min-w-[2.5rem] shrink-0 text-right text-xs font-semibold tabular-nums">
          {formatMediaDurationFromSeconds(shownTime)}
        </span>

        {/* Fixed width: "1×", "1.5×" and "2×" differ in width and a pill that resized on every click jumped the row. */}
        <button
          type="button"
          onClick={cyclePlaybackRate}
          aria-label="Prędkość odtwarzania"
          className={cn(
            "inline-flex h-5 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full border-0 p-0 text-[11px] font-bold leading-none tabular-nums text-current transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-current",
            own ? "bg-gray-300/70 hover:bg-gray-300" : "bg-white/20 hover:bg-white/30",
          )}
        >
          {playbackRate}×
        </button>
      </div>
    </div>
  );
};
